import { computed, Injectable, signal } from "@angular/core";
import { stringOrNull } from "blu-utilities";
import { COOKIES } from "src/app/core/models/cookie";
import { BluStorageService } from "src/app/core/services/blu-storage.service";
import { CommunicationService } from "src/app/core/services/communication.service";
import { BroadcastMessages } from "src/app/shared/general/constants";
import { UserAccessResponse } from "../models/login";

@Injectable({
  providedIn: "root",
})
export class AccountService {
  // Store user access token in a signal to trigger automatic updates
  private readonly userAccessToken = signal<stringOrNull>(null);

  // Computed signal for authentication state
  readonly isLoggedIn = computed(() => !!this.userAccessToken());
  // Get access token
  readonly getUserAccessToken = computed(() => this.userAccessToken());

  constructor(
    private readonly bluStorageService: BluStorageService,
    private communicationService: CommunicationService,
  ) {}

  /**
   * Set user access token and trigger signal update
   */
  setUserAccessToken(value: stringOrNull): void {
    this.userAccessToken.set(value);
  }

  setRefreshToken(value: stringOrNull) {
    this.bluStorageService.setLocalStorage(COOKIES.REFRESH_TOKEN, value);
  }

  /**
   * Get refresh token from cookies
   */
  getRefreshTokenFromCookie(): stringOrNull {
    return this.bluStorageService.hasInLocalStorage(COOKIES.REFRESH_TOKEN)
      ? this.bluStorageService.getFromLocalStorage(COOKIES.REFRESH_TOKEN)
      : null;
  }

  /**
   * Determines whether the user's session can be restored using a refresh token.
   * This check is only relevant after a tab reload to decide if a refresh token request should be sent.
   *
   * @returns {boolean} True if the user is not logged in but has a valid refresh token stored in cookies.
   */
  canRestoreSessionFromRefreshToken(): boolean {
    return !this.isLoggedIn() && this.isRefreshTokenInCookie();
  }

  /**
   * Check if refresh token exists in cookies
   */
  isRefreshTokenInCookie(): boolean {
    return !!this.getRefreshTokenFromCookie();
  }

  setTokens(data: UserAccessResponse) {
    const userAccessToken = data.token;
    this.setUserAccessToken(userAccessToken);
    this.sendTokenToOtherTabs(userAccessToken);
    this.setRefreshToken(data.refreshToken);
  }

  private sendTokenToOtherTabs(userAccessToken: string) {
    if (userAccessToken) {
      this.communicationService.sendMessage({
        data: userAccessToken,
        type: BroadcastMessages.TOKENS_UPDATED,
      });
    }
  }
}
